import {
  ManualOrderPopup,
  CreateShipmentLabel,
  OrderList,
  CreateBatchPopup,
  BatchOrderList,
  PrintLabelPopup,
  Header,
} from '@page-components/order';
import { marketplaceRegionHook } from '@hooks/marketplace-region';
import { useConnections } from '@features/rebuild-settings';
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { BodyContainer, FlexRow } from '@elements/Common';
import {
  CommonConstant,
  ErrorMessageConstant,
  OrderStatus,
} from '@utility/constants/common';
import { marketplaceListHook } from '@hooks/list';
import { useSidebar } from '@contexts/sidebar';
import { ToastMessage } from '@utility/toast';
import BatchOrdersService from '@utility/services/batch-order';
import { CommonUtility } from '@utility/common';
import { AddressValidationPopup } from '@components/AddressValidationPopup';
import {
  EqualsIcon,
  FactoryIcon,
  GlobeSimpleIcon,
  PackageIcon,
  ShoppingCartIcon,
  ShoppingCartSimpleIcon,
  StackIcon,
  UserCircleIcon,
  CaretLeftIcon,
  CaretRightIcon,
  CalendarCheckIcon,
  CalendarPlusIcon,
} from '@phosphor-icons/react';
import { dropdownOptionsIncludeFlag } from '@elements/Flag';
import { filterTypes } from '@components/Filter';
import {
  BatchSelectionBar,
  canBatchOrder,
  canBatchWith,
  canFulfillOrder,
  canStartBatching,
  canStartFulfilling,
  FulfillSelectionBar,
  getBatchTargets,
  getFulfillQueue,
  getOrderProvider,
  isOrderBusy,
  readFiltersFromUrl,
  toFilterOptions,
  usePageUrlSync,
  writeFiltersToUrl,
  findV2OrderByScan,
  v2MarketplaceOptions,
} from '@features/orders';
import type { OrderNavigationState } from '@features/orders/lib/order-navigation';
import { useUserHook } from '@hooks/user';
import { useDebouncedMemo } from '@hooks/debounceAsync';
import { useProviderOptions } from '@features/storage-providers';
import { ExportOrderPopup } from '@page-components/order/ExportOrderPopup';
import ShippingLabelService from '@utility/services/shipping-label';
import { useBarcodeScanner } from '@hooks/barcode';
import { useOrdersV2 } from '@features/orders/hooks/orders-v2';
import { ImportOrderPopup } from '../importOrderPopup';
import { ConfirmFulfillPopup } from '../ConfirmFulfillPopup';
import { ShippingLabelContainer } from './common';
import { getMarketplaceFilterOptions } from '../common';

export function DesktopOrderList({
  batchOrderData,
  batchOrderLoading,
  batchUpdate,
  data,
  ddFilter,
  error,
  filter,
  filterChanged,
  firebaseData,
  loading,
  manualOrderPopupClose,
  pageChanged,
  passedOrderId,
  refetchOrderData,
  selectedOrder,
  setBatchOrderData,
  setData,
  setSelectedOrder,
  setShowOrderPopup,
  setTableSearch = () => {},
  showOrderPopup,
  status,
  syncOrderForFirebase,
  totalData,
  totalPages,
  updateAllFilter,
}) {
  const navigate = useNavigate();
  /* EN-4117: the new orders page is read-only for now. */
  const { isV2 } = useOrdersV2();
  const params = useParams();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  /* Set right before a pagination click's own page-state update, so the URL
   * write it triggers pushes a history entry instead of replacing the top
   * one — that is what lets the browser's back/forward step through pages. */
  const pushPageRef = useRef(false);
  const [orderError, setOrderError] = useState('');
  const [showBatchPopup, setShowBatchPopup] = useState(false);
  const [showConnectedPrinter, setShowConnectedPrinter] = useState(false);
  const [printURL, setPrintUrl] = useState('');
  const {
    data: regionData,
    loading: regionLoading,
    regionId,
  } = marketplaceRegionHook(true);
  // @ts-expect-error - TS2339: Property 'refreshSidebar' does not exist on type 'unknown'.
  const { refreshSidebar } = useSidebar();
  /* Legacy marketplaces (Mongo) for a legacy account; a V2 account's come from its connections. */
  const { data: marketplaceData, loading: marketplaceLoading } =
    marketplaceListHook(true, true, !isV2);
  const { data: v2Connections } = useConnections();
  const [currentOrder, setCurrentOrder] = useState(null);
  const [addressPopup, setAddressPopup] = useState(false);
  const [isPickAndShip, setIsPickAndShip] = useState(false);
  const [openExportPopup, setOpenExportPopup] = useState(false);
  const [openImportPopup, setOpenImportPopup] = useState(false);
  const { data: spList, loading: spLoading } = useProviderOptions(true);

  const [scanLoading, setScanLoading] = useState(false);

  const [editBatch, setEditBatch] = useState(null);
  const [batchToFulfill, setBatchToFulfill] = useState(null);

  const createPopup = searchParams.get('createPopup');

  const {
    allUsersOptions,
    loading: userLoading,
    searchFunction: userSearchFunction,
  } = useUserHook();

  useEffect(() => {
    if (createPopup === 'true') {
      setShowOrderPopup(true);
    }
  }, [createPopup]);

  const showBatchOrder = useMemo(
    () => [OrderStatus.PendingShipment, OrderStatus.Shipped].includes(status),
    [status],
  );

  const batchOrders = useMemo(
    () =>
      (batchOrderData || []).map(batch => {
        const arr = batch.orders.map(order => {
          const temp = firebaseData.find(x => x.order_id === order._id);
          if (temp) {
            return {
              loading: temp.status === 'pending',
              ...order,
              ...temp,
            };
          }
          return order;
        });
        return { ...batch, orders: arr };
      }),
    [batchOrderData, firebaseData],
  );

  useEffect(() => {
    if (passedOrderId && data.some(x => x._id === passedOrderId)) {
      navigate(`/app/orders/${encodeURIComponent(passedOrderId)}`);
    }
  }, [passedOrderId, data, navigate]);

  const [selectedBatchOrder, setSelectedBatchOrder] = useState({});
  const [allSelected, setAllSelected] = useState(false);

  const [savingBatch, setSavingBatch] = useState(false);

  const [selectionMode, setSelectionMode] = useState<
    'batch' | 'fulfill' | null
  >(null);
  const isSelecting = selectionMode === 'batch';

  const batchProviderId = useMemo(() => {
    const first = Object.keys(selectedOrder)[0];
    return first ? selectedOrder[first] : undefined;
  }, [selectedOrder]);

  const batchProviderName = useMemo(() => {
    const first = Object.keys(selectedOrder)[0];
    const order = first ? data.find(x => x._id === first) : undefined;
    return order ? getOrderProvider(order)?.name : undefined;
  }, [selectedOrder, data]);

  const currentBatchShippingLabel = useMemo(
    () =>
      Object.keys(selectedBatchOrder).length === 1
        ? Object.keys(selectedBatchOrder)[0]
        : '',
    [selectedBatchOrder],
  );

  const canBatchThisPage = useMemo(
    () => status === OrderStatus.PendingShipment && canStartBatching(data),
    [status, data],
  );

  const canFulfillThisPage = useMemo(
    () => status === OrderStatus.PendingShipment && canStartFulfilling(data),
    [status, data],
  );

  const batchTargets = useMemo(
    () => getBatchTargets(batchOrders, batchProviderId),
    [batchOrders, batchProviderId],
  );

  /* Whatever the page selected for other reasons is not the start of a batch. */
  const startSelecting = (mode: 'batch' | 'fulfill') => {
    setSelectedOrder({});
    setAllSelected(false);
    setSelectionMode(mode);
  };

  const cancelSelecting = () => {
    setSelectionMode(null);
    setSelectedOrder({});
    setAllSelected(false);
  };

  const toggleSelection = item => {
    setSelectedBatchOrder({});

    if (selectedOrder[item._id]) {
      const rest = { ...selectedOrder };
      delete rest[item._id];
      setSelectedOrder(rest);
      return;
    }

    /* Fulfilling is per order, so there is no warehouse to agree on. */
    if (selectionMode === 'fulfill') {
      setSelectedOrder({
        ...selectedOrder,
        [item._id]: true,
      });
      return;
    }

    /*
     * The provider id is what every other tick is matched against, so an order
     * without one cannot open a batch. It used to store `true` here instead,
     * which then compared unequal to every real provider and left the mode
     * unusable until it was cancelled.
     */
    const providerId = getOrderProvider(item)?._id;

    if (!providerId) {
      return;
    }

    setSelectedOrder({
      ...selectedOrder,
      [item._id]: providerId,
    });
  };

  const startFulfillQueue = () => {
    const orderIds = getFulfillQueue(data, selectedOrder);
    const [firstId] = orderIds;

    if (!firstId) {
      return;
    }

    const state: OrderNavigationState = {
      orderIds,
      listUrl: `${location.pathname}${location.search}`,
      mode: 'fulfill',
    };

    cancelSelecting();
    navigate(`/app/orders/${encodeURIComponent(firstId)}`, { state });
  };

  /*
   * Select-all answers the same question as a row's own checkbox — `batching`
   * owns the rule, so the two cannot drift apart and build a batch the rows
   * would have refused.
   */
  const toggleAllSelection = value => {
    setAllSelected(value);

    if (!value) {
      setSelectedOrder({});
      return;
    }

    if (selectionMode === 'fulfill') {
      const picked = {};
      for (const order of data.filter(canFulfillOrder)) {
        picked[order._id] = true;
      }
      setSelectedOrder({ ...selectedOrder, ...picked });
      return;
    }

    const candidates = data.filter(x => canBatchOrder(x) && !isOrderBusy(x));
    const [first] = candidates;
    /* A batch already started fixes the warehouse; otherwise the first pick does. */
    const providerId =
      batchProviderId || (first ? getOrderProvider(first)?._id : undefined);

    if (!providerId) {
      ToastMessage.error('No order with valid fulfillment provider found');
      return;
    }

    const picked = {};
    for (const order of candidates) {
      if (canBatchWith(order, providerId)) {
        picked[order._id] = providerId;
      }
    }

    setSelectedOrder({
      ...selectedOrder,
      ...picked,
    });
  };

  const toggleBatchOrderSelection = (id, value, error) => {
    setSelectedOrder({});
    setOrderError('');
    if (selectedBatchOrder[id]) {
      const rest = { ...selectedBatchOrder };
      delete rest[id];
      setSelectedBatchOrder(rest);
    } else {
      setSelectedBatchOrder({
        [id]: value,
      });
    }
    setOrderError(error);
  };

  const batchOrderLabelDone = (_, orderId) => {
    setOrderError('');
    const batchId = selectedBatchOrder[currentBatchShippingLabel];
    let nextOrder = null;
    const newBatchData = batchOrders.map(item => {
      if (item._id === batchId) {
        let totalCompleted = 0;
        const newOrders = item.orders.map(order => {
          if (order._id === currentBatchShippingLabel) {
            totalCompleted += 1;
            return {
              ...order,
              status: 'completed',
              label_generated: true,
            };
          }
          if (order.label_generated) {
            totalCompleted += 1;
          }
          return order;
        });
        const completed = totalCompleted === newOrders.length;

        if (!completed) {
          const currentIndex = newOrders.findIndex(
            x => x._id === currentBatchShippingLabel,
          );
          if (currentIndex === newOrders.length - 1) {
            nextOrder = newOrders.find(x => !x.label_generated)._id;
          } else {
            nextOrder = newOrders[currentIndex + 1]._id;
          }
        }

        return {
          ...item,
          orders: newOrders,
          status: `${CommonUtility.roundNumber(
            (totalCompleted * 100) / newOrders.length,
          )}%`,
          completed,
        };
      }
      return item;
    });
    setBatchOrderData(newBatchData);
    if (orderId) {
      syncOrderForFirebase(orderId);
    }
    if (nextOrder) {
      setSelectedBatchOrder({
        [nextOrder]: batchId,
      });
    } else {
      setSelectedBatchOrder({});
    }

    refreshSidebar();
  };

  const orderChanged = order => {
    toggleAllSelection(false);
    filterChanged('sort', order);
  };

  const batchPopupClose = (result, orderUpdate = true) => {
    setShowBatchPopup(false);
    setEditBatch(null);
    if (result) {
      batchUpdate();
      refreshSidebar();
      cancelSelecting();
      if (orderUpdate) {
        refetchOrderData();
      }
    }
  };

  const openPrintPopup = url => {
    setPrintUrl(url);
    setShowConnectedPrinter(true);
    setIsPickAndShip(true);
  };

  const pickAndShipClick = async item => {
    setShowConnectedPrinter(item);
  };

  const deleteBatch = async item => {
    try {
      setSavingBatch(true);
      await BatchOrdersService.remove(item._id);
      refreshSidebar();
      const temp = batchOrders.filter(batch => batch._id !== item._id);
      setBatchOrderData(temp);
      if (
        currentBatchShippingLabel &&
        selectedBatchOrder[currentBatchShippingLabel] === item._id
      ) {
        setSelectedBatchOrder({});
      }
      refetchOrderData();
    } catch (error) {
      ToastMessage.error(error.error || ErrorMessageConstant.defaultAPIError);
    } finally {
      setSavingBatch(false);
    }
  };

  const deleteOrder = async (item, batchId) => {
    try {
      setSavingBatch(true);
      await BatchOrdersService.update(null, {
        action_type: 'remove',
        orders: [item._id],
        batch_id: batchId,
      });
      refreshSidebar();
      const tempBatchData = batchOrders.map(batch => {
        if (batch._id === batchId) {
          const t = { ...batch };
          t.orders = t.orders.filter(x => x._id !== item._id);
          t.total_items = t.orders.reduce((a, b) => a + b.total_items, 0);
          t.total_orders = t.orders.length;
          t.completed =
            t.orders.filter(x => x.status === 'completed').length ===
            t.orders.length;
          t.status = `${CommonUtility.roundNumber(
            (t.orders.filter(x => x.status === 'completed').length * 100) /
              t.orders.length,
          )}%`;
          return t;
        }
        return batch;
      });
      const temp = tempBatchData.filter(batch => batch.orders.length > 0);
      setBatchOrderData(temp);
      if (
        currentBatchShippingLabel &&
        Object.keys(selectedBatchOrder).includes(item._id)
      ) {
        setSelectedBatchOrder({});
      }
      setData([
        {
          ...item,
          state: OrderStatus.PendingShipment,
          updated: true,
        },
        ...data,
      ]);
    } catch (error) {
      ToastMessage.error(error.error || ErrorMessageConstant.defaultAPIError);
    } finally {
      setSavingBatch(false);
    }
  };

  const addToExistingBatch = async value => {
    try {
      setSavingBatch(true);
      const result = await BatchOrdersService.update(null, {
        action_type: 'add',
        orders: Object.keys(selectedOrder),
        batch_id: value,
      });
      ToastMessage.success(result.data);
      batchPopupClose(true);
    } catch (error) {
      ToastMessage.error(error.error || ErrorMessageConstant.defaultAPIError);
    } finally {
      setSavingBatch(false);
    }
  };

  const currency = useMemo(
    () =>
      regionData?.find((x: any) => x.value === ddFilter.marketplace_region_id)
        ?.currency_code,
    [ddFilter.marketplace_region_id, regionData],
  );

  const updateBatchOrderPickList = (url, batchId) => {
    setPrintUrl(url);
    setBatchOrderData(batchOrders.filter(x => x._id !== batchId));
  };

  const pageSizeChanged = size => {
    toggleAllSelection(false);
    filterChanged('limit', size);
  };

  const listpPageChanged = page => {
    toggleAllSelection(false);
    pushPageRef.current = true;
    pageChanged(page);
  };

  const validateAddress = order => {
    setCurrentOrder({
      ...order,
      customer_details: {
        address_one: order.shipping_address.addressLine1,
        address_two: order.shipping_address.addressLine2 || '',
        city: order.shipping_address.city,
        state: order.shipping_address.stateOrRegion,
        postal_code: order.shipping_address.postalCode,
        country: order.shipping_address.countryCode,
        name: order.shipping_address.name,
        email: order.shipping_address.e_mail,
        phone: order.shipping_address.phoneNumber,
      },
    });
    setAddressPopup(true);
  };

  const closeValidateAddress = result => {
    if (result) {
      refetchOrderData();
    }
    setCurrentOrder(null);
    setAddressPopup(false);
  };

  const onValidateAddress = result => {
    const temp = data.map(item => {
      if (item._id === currentBatchShippingLabel) {
        return {
          ...item,
          invalid_address: false,
          shipping_address: result,
          order_error: (item.order_error || []).map(oe => {
            if (oe.reason === 'Invalid Address') {
              return {
                ...oe,
                resolved: true,
              };
            }
            return oe;
          }),
        };
      }
      return item;
    });
    setData(temp);
  };

  const currentBatchOrderIsInList = useMemo(
    () =>
      !!batchOrders.find(batch =>
        batch.orders?.find(order => order._id === currentBatchShippingLabel),
      ),
    [currentBatchShippingLabel, batchOrders],
  );

  useBarcodeScanner(scan => checkOrderScan(scan));

  const checkOrderScan = useCallback(
    async scan => {
      if (batchToFulfill || showBatchPopup || editBatch) {
        return;
      }

      if (currentBatchShippingLabel && currentBatchOrderIsInList) {
        return;
      }
      try {
        setScanLoading(true);
        if (isV2) {
          const found = await findV2OrderByScan(scan);
          if (found) navigate(`/app/orders/${found}`);
          else ToastMessage.error(`No order found for ${scan}`);
          return;
        }
        const res = await ShippingLabelService.checkOrderScan(scan);
        if (res?.data?._id) {
          navigate(`/app/orders/${res?.data?._id}`);
        }
      } catch (error) {
        ToastMessage.apiError(error);
      } finally {
        setScanLoading(false);
      }
    },
    [
      currentBatchShippingLabel,
      currentBatchOrderIsInList,
      batchToFulfill,
      showBatchPopup,
      editBatch,
      isV2,
    ],
  );

  const optionsMarketplace = useMemo(
    () =>
      isV2
        ? v2MarketplaceOptions(v2Connections)
        : getMarketplaceFilterOptions(marketplaceData),
    [isV2, v2Connections, marketplaceData],
  );

  /* Read once: after this the query is owned by the filters. */
  // biome-ignore lint/correctness/useExhaustiveDependencies: mount only, on purpose
  useEffect(() => {
    updateAllFilter(readFiltersFromUrl(searchParams));
  }, []);

  /* Covers what the mount read above does not: a direct edit of `page` in
   * the address bar, or the browser's own back/forward, once the list is
   * already open. */
  usePageUrlSync({ currentPage: filter.page, pageChanged, searchParams });

  const marketplaceOptions = useMemo(
    () => toFilterOptions(optionsMarketplace),
    [optionsMarketplace],
  );
  const regionOptions = useMemo(
    () => toFilterOptions(dropdownOptionsIncludeFlag(regionData)),
    [regionData],
  );
  const userOptions = useMemo(
    () => toFilterOptions(allUsersOptions),
    [allUsersOptions],
  );
  /* Only the queues where an order has a provider to filter by. */
  const providerOptions = useMemo(
    () =>
      status === OrderStatus.PendingFulfillment ||
      status === OrderStatus.PendingShipment
        ? toFilterOptions(spList)
        : undefined,
    [spList, status],
  );

  const filters = useDebouncedMemo(
    () => {
      if (userLoading) return [];

      const temp: Record<string, any> = {
        ...params,
        ...Object.fromEntries(searchParams),
      };
      Object.keys(temp).forEach(key => {
        const final = CommonUtility.tryParseJSONObject(String(temp[key] || ''));
        if (final) {
          temp[key] = final;
        }
      });

      const defaultValues = {
        ...temp,
      };

      const isPendingFulfillment = status === OrderStatus.PendingFulfillment;
      const calender_options = [
        {
          value: 'EQUAL',
          key: 'EQUAL',
          text: 'Equals',
          conditionImage: <EqualsIcon size={8} weight="bold" />,
        },
        { value: 'BETWEEN', key: 'BETWEEN', text: 'Between' },
        {
          value: 'LESS_THAN',
          key: 'LESS_THAN',
          text: 'Before',
          conditionImage: <CaretLeftIcon size={8} weight="bold" />,
        },
        {
          value: 'GREATER_THAN',
          key: 'GREATER_THAN',
          text: 'After',
          conditionImage: <CaretRightIcon size={8} weight="bold" />,
        },
      ];

      const getDefaultValue = value => {
        if (value?.includes(',')) {
          return value.split(',');
        }
        return value;
      };
      const arr = [
        {
          name: 'marketplace',
          label: 'Marketplaces',
          icon: <ShoppingCartSimpleIcon />,
          options: optionsMarketplace,
          multiple: true,
          search: true,
        },
        {
          search: true,
          custom_options: [
            {
              value: 'EQUAL',
              key: 'EQUAL',
              text: 'Equals',
              conditionImage: <EqualsIcon size={8} weight="bold" />,
            },
          ],
          name: 'marketplace_region_id',
          label: 'Region',
          icon: <GlobeSimpleIcon />,
          options: dropdownOptionsIncludeFlag(regionData),
          defaultValue: defaultValues && defaultValues.marketplace_region_id,
          ignoreDefault: -1,
        },
        {
          name: 'items_total_qty',
          label: 'Items Quantity',
          icon: <PackageIcon />,
          type: 'text',
          numeric: true,
          defaultValue: defaultValues && defaultValues.items_total_qty,
        },
        {
          multiple: true,
          name: 'created_by',
          label: 'Created By',
          icon: <UserCircleIcon />,
          search: true,
          customSearch: userSearchFunction,
          options: allUsersOptions,
          typeQuery: 'created_by_op',
          defaultValue: defaultValues && defaultValues.created_by,
        },
        {
          name: 'handling_time',
          label: 'Shipping Date',
          icon: <CalendarPlusIcon />,
          custom_options: calender_options,
          type: 'date',
          defaultValue:
            defaultValues && getDefaultValue(defaultValues.handling_time),
          typeQuery: 'handling_time_op',
          defaultQuery: defaultValues && defaultValues.handling_time_op,
        },
        {
          name: 'shipping_time',
          label: 'Delivery Date',
          icon: <CalendarCheckIcon />,
          custom_options: calender_options,
          type: 'date',
          defaultValue:
            defaultValues && getDefaultValue(defaultValues.shipping_time),
          typeQuery: 'shipping_time_op',
          defaultQuery: defaultValues && defaultValues.shipping_time_op,
        },
        {
          skip: true,
          name: 'page',
          must: true,
          defaultValue: (defaultValues && defaultValues.page) || 1,
          ignoreDefault: 1,
        },
        {
          skip: true,
          name: 'limit',
          must: true,
          defaultValue:
            defaultValues && CommonUtility.isValidNumber(defaultValues.limit)
              ? Number(defaultValues.limit)
              : 50,
          ignoreDefault: 50,
        },
        {
          skip: true,
          name: 'sort',
          must: true,
          defaultValue: (defaultValues && defaultValues.sort) || {
            time_acknowledged: CommonConstant.sortDirection.descending,
          },
          ignoreDefault: {
            time_acknowledged: CommonConstant.sortDirection.descending,
          },
        },
      ];

      if (isPendingShipment) {
        arr.push({
          skip: true,
          name: 'oos',
          must: true,
          defaultValue: (defaultValues && defaultValues.oos) || false,
          ignoreDefault: false,
        } as any);
      }

      if (isPendingFulfillment || isPendingShipment) {
        arr.push({
          search: true,
          name: 'fulfillment_provider_id',
          label: 'Fulfillment Provider',
          icon: <FactoryIcon />,
          options: spList,
          defaultValue: defaultValues && defaultValues.fulfillment_provider_id,
          ignoreDefault: -1,
          // @ts-expect-error - TS2353: Object literal may only specify known properties, and 'sorted' does not exist in...
          sorted: false,
        });
      }
      return arr;
    },
    [optionsMarketplace, regionData, allUsersOptions, spList, status],
    500,
  );

  useEffect(() => {
    if (!filters || filters.length < 1) {
      return;
    }

    const urlObject = {
      status,
    };

    if (searchParams.get('videoId')) {
      // @ts-expect-error - TS2339: Property 'videoId' does not exist on type '{ status: any; }'.
      urlObject.videoId = searchParams.get('videoId');
    }

    Object.keys(ddFilter).forEach(filter => {
      const findFilter = filters.find(x => x.name === filter);
      if (filter === 'sort') {
        const sortValue = JSON.stringify(ddFilter[filter]);
        if (sortValue !== JSON.stringify(findFilter?.ignoreDefault)) {
          urlObject[filter] = sortValue;
        }
      } else if (
        ddFilter[filter] &&
        !(filter.includes('op') && ddFilter[filter] === filterTypes.is) &&
        findFilter?.ignoreDefault !== ddFilter[filter]
      ) {
        urlObject[filter] = ddFilter[filter];
      }
    });

    const searchString = new URLSearchParams(
      urlObject as Record<string, string>,
    ).toString();
    writeFiltersToUrl(navigate, location.pathname, searchString, pushPageRef);
  }, [ddFilter, filters]);

  const isPendingShipment = status === OrderStatus.PendingShipment;

  const fulfillBatch = batch => {
    setBatchToFulfill(batch);
  };

  const closeSidebar = () => {
    if (currentBatchShippingLabel) {
      setSelectedBatchOrder(prev => {
        // @ts-expect-error - TS2538: Type 'any' cannot be used as an index type.
        const { [currentBatchShippingLabel]: _, ...rest } = prev;
        return rest;
      });
    }
    setOrderError('');
  };

  return (
    <>
      <Header
        ddFilter={ddFilter}
        marketplaceLoading={marketplaceLoading}
        newOrderClick={() => setShowOrderPopup(true)}
        regionLoading={regionLoading}
        setTableSearch={setTableSearch}
        updateAllFilter={updateAllFilter}
        setOpenExportPopup={setOpenExportPopup}
        setOpenImportPopup={setOpenImportPopup}
        status={status}
        marketplaceOptions={marketplaceOptions}
        regionOptions={regionOptions}
        providerOptions={providerOptions}
        userOptions={userOptions}
        spLoading={spLoading}
        isPendingShipment={isPendingShipment}
        filter={filter}
        filterChanged={filterChanged}
      />
      <BodyContainer>
        {!isV2 && showBatchOrder && batchOrders.length > 0 && (
          <>
            <div className="sk:flex! sk:items-center! sk:mt-5! sk:mb-2!">
              <div className="sk:flex! sk:items-center!">
                <StackIcon size={25} className="sk:text-fg-tertiary" />
                <h3 className="sk:m-0! sk:ml-2!">Batches</h3>
              </div>
            </div>
            <BatchOrderList
              toggleSelection={toggleBatchOrderSelection}
              selectedOrder={selectedBatchOrder}
              data={batchOrders}
              openPrintPopup={openPrintPopup}
              pickAndShipClick={pickAndShipClick}
              saving={savingBatch}
              deleteBatch={deleteBatch}
              deleteOrder={deleteOrder}
              loading={batchOrderLoading}
              status={status}
              fulfillBatch={fulfillBatch}
              editBatch={setEditBatch}
            />
            <div className="sk:mb-3.5!" />
          </>
        )}
        {/* Rendered only when it holds something: an empty row still carries
            its bottom margin. */}
        {(!isV2 && showBatchOrder && batchOrders.length > 0) ||
        selectionMode ||
        canBatchThisPage ||
        canFulfillThisPage ? (
          <div className="sk:flex! sk:justify-between! sk:mb-2!">
            {/* Gap rather than a margin on the bars: they also show on their own,
                with no heading to be indented from. */}
            <div
              className="sk:flex! sk:items-center!"
              style={{ gap: '0.5rem' }}
            >
              {showBatchOrder && batchOrders.length > 0 && (
                <div className="sk:flex! sk:items-center!">
                  <ShoppingCartIcon className="sk:text-fg-tertiary" size={25} />
                  <h3 className="sk:my-0! sk:ml-2!">Orders</h3>
                </div>
              )}
              <FlexRow style={{ gap: '0.5rem' }}>
                {selectionMode !== 'fulfill' && (
                  <BatchSelectionBar
                    isSelecting={isSelecting}
                    canSelect={canBatchThisPage}
                    onStart={() => startSelecting('batch')}
                    onCancel={cancelSelecting}
                    selectedCount={Object.keys(selectedOrder).length}
                    providerName={batchProviderName}
                    onCreate={() => setShowBatchPopup(true)}
                    targets={batchTargets}
                    onAddToBatch={addToExistingBatch}
                    isBusy={savingBatch || batchOrderLoading}
                  />
                )}
                {!isSelecting && (
                  <FulfillSelectionBar
                    isSelecting={selectionMode === 'fulfill'}
                    canSelect={canFulfillThisPage}
                    onStart={() => startSelecting('fulfill')}
                    onCancel={cancelSelecting}
                    selectedCount={Object.keys(selectedOrder).length}
                    onFulfill={startFulfillQueue}
                    isBusy={savingBatch || batchOrderLoading}
                  />
                )}
              </FlexRow>
            </div>
          </div>
        ) : null}
        <OrderList
          status={status}
          currency={currency}
          loading={loading}
          data={data}
          currentPage={filter.page}
          pageChanged={listpPageChanged}
          pageSize={filter.limit}
          pageSizeChanged={pageSizeChanged}
          totalPages={totalPages}
          totalData={totalData}
          toggleSelection={toggleSelection}
          selectedOrder={selectedOrder}
          selectionMode={selectionMode}
          sort={filter.sort}
          search={filter?.search?.trim()}
          sortChanged={orderChanged}
          error={error}
          toggleAllSelection={toggleAllSelection}
          allSelected={allSelected}
          validateAddress={validateAddress}
        />
        {!isV2 && currentBatchShippingLabel && currentBatchOrderIsInList && (
          <ShippingLabelContainer onClose={closeSidebar}>
            <CreateShipmentLabel
              orderId={currentBatchShippingLabel}
              batchId={selectedBatchOrder[currentBatchShippingLabel]}
              error={orderError}
              // @ts-expect-error - TS2322: Type '(_: any, orderId: any) => void' is not assignable to type '() => void'.
              createLabelDone={batchOrderLabelDone}
              // @ts-expect-error - TS2322: Type '(result: any) => void' is not assignable to type '() => void'.
              onValidateAddress={onValidateAddress}
              noShadow
              onClose={closeSidebar}
            />
          </ShippingLabelContainer>
        )}
      </BodyContainer>
      {/* @ts-expect-error - TS2741: Property 'customer' is missing in type '{ open: any; closeMo... */}
      <ManualOrderPopup
        open={showOrderPopup && !isV2}
        closeModal={result => manualOrderPopupClose(result)}
        closeManual={() => {
          setShowOrderPopup(false);
        }}
      />
      <ImportOrderPopup
        open={openImportPopup && !isV2}
        onClose={() => setOpenImportPopup(false)}
        regionId={regionId}
      />
      <ExportOrderPopup
        open={openExportPopup}
        onClose={() => setOpenExportPopup(false)}
      />
      <CreateBatchPopup
        open={!isV2 && (showBatchPopup || !!editBatch)}
        closeModal={batchPopupClose}
        selectedOrders={selectedOrder}
        orders={data}
        batchToEdit={editBatch}
      />
      <ConfirmFulfillPopup
        open={!isV2 && !!batchToFulfill}
        batchToFulfill={batchToFulfill}
        closeModal={() => setBatchToFulfill(null)}
      />
      {/* @ts-expect-error - TS2741: Property 'name' is missing in type '{ open: boolean; item: b... */}
      <PrintLabelPopup
        open={showConnectedPrinter}
        item={showConnectedPrinter}
        closeModal={() => {
          setShowConnectedPrinter(false);
          // @ts-expect-error - TS2345: Argument of type 'null' is not assignable to parameter of type 'SetStateAction<s...
          setPrintUrl(null);
          setIsPickAndShip(false);
        }}
        printURL={printURL}
        updateBatchOrderPickList={updateBatchOrderPickList}
        generateLabelText={isPickAndShip ? 'Batch Pick List' : 'Generate Label'}
      />
      {/* @ts-expect-error - TS2739: Type '{ open: boolean; closeModal: (result: any) => void; cu... */}
      <AddressValidationPopup
        open={addressPopup}
        closeModal={closeValidateAddress}
        // @ts-expect-error - TS2339: Property 'customer_details' does not exist on type 'never'.
        currentAddressData={currentOrder?.customer_details}
        // @ts-expect-error - TS2339: Property '_id' does not exist on type 'never'.
        orderId={currentOrder?._id}
        // @ts-expect-error - TS2339: Property 'marketplace' does not exist on type 'never'.
        notManual={currentOrder?.marketplace?.type !== 'manual'}
      />
    </>
  );
}
